import React from "react";
import { useMemberAuth } from "@/lib/memberAuth";
import { MapPin, Globe, EnvelopeSimple, ShieldCheck } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 py-3 border-b border-black/5 last:border-b-0">
    <div className="overline text-navy/60">{label}</div>
    <div className="text-sm text-navy/90" data-testid={testid}>{children || <span className="text-navy/40">—</span>}</div>
  </div>
);

export default function MemberProfile() {
  const { member } = useMemberAuth();

  return (
    <div data-testid="portal-profile-page">
      <div className="mb-6">
        <div className="overline mb-2">My profile</div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight">{member.company_name}</h1>
        <p className="text-navy/70 mt-1 text-sm flex items-center gap-1.5">
          <MapPin size={14} /> {member.city ? `${member.city}, ` : ""}{member.country}
        </p>
      </div>

      <div className="bg-white border border-black/10 p-6">
        <Row label="Company" testid="profile-company">{member.company_name}</Row>
        <Row label="Country" testid="profile-country">{member.country}</Row>
        <Row label="City" testid="profile-city">{member.city}</Row>
        <Row label="Territory" testid="profile-territory">{member.territory}</Row>
        <Row label="Membership type" testid="profile-type">{member.membership_type?.replace("_", " ")}</Row>
        <Row label="Renewal" testid="profile-renewal">{member.renewal_date}</Row>
        <Row label="Contact email" testid="profile-email">
          {member.contact_email && (
            <span className="inline-flex items-center gap-1.5 text-navy">
              <EnvelopeSimple size={14} /> {member.contact_email}
            </span>
          )}
        </Row>
        <Row label="Website" testid="profile-website">
          {member.website && (
            <a href={member.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-navy hover:text-ember">
              <Globe size={14} /> {member.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </Row>
        <Row label="Services" testid="profile-services">
          {(member.services || []).length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {(member.services || []).map((s) => (
                <span key={s} className="text-[10px] uppercase tracking-widest border border-navy/20 px-1.5 py-0.5 text-navy/70">{s}</span>
              ))}
            </div>
          ) : null}
        </Row>
        <Row label="Trade lanes" testid="profile-lanes">{member.trade_lanes}</Row>
        <Row label="About" testid="profile-about">{member.about}</Row>
      </div>

      <div className="mt-8 bg-white border border-black/10 p-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={18} weight="duotone" className="text-ember" />
          <div className="overline">Security</div>
        </div>
        <p className="text-sm text-navy/70 leading-relaxed">
          EPAS never emails passwords in plaintext. To change your password, request a one-time secure link — it
          expires in 24 hours and can only be used once.
        </p>
        <Link
          to="/portal/forgot-password"
          data-testid="profile-change-password-link"
          className="mt-4 inline-flex btn-secondary"
        >
          Change my password
        </Link>
        <p className="mt-5 text-[11px] text-navy/50 leading-relaxed">
          To update your profile data (services, trade lanes, about text), email{" "}
          <a href="mailto:rok@epas.one" className="underline hover:text-ember">rok@epas.one</a> — every change is logged in
          the operator audit trail.
        </p>
      </div>
    </div>
  );
}
